export interface IApiEndpointConfig {
    envUrl: string | undefined;
    localUrl: string;
}

interface IApiEndpointEnv {
    VITE_APP_MAIN_API?: string;
    VITE_APP_STATIC_API?: string;
    VITE_APP_USERSETTINGS_API?: string;
    VITE_APP_SERIES_API?: string;
    VITE_APP_EPEX_API?: string;
    VITE_APP_EPEX_FRANCE_API?: string;
    VITE_APP_SIGNAL_R?: string;
    VITE_APP_ENTSOE_API?: string;
    VITE_APP_IRELAND_API?: string;
    VITE_APP_FRANCE_API?: string;
    VITE_APP_BELGIUM_API?: string;
    VITE_APP_NETHERLANDS_API?: string;
    VITE_APP_DYNAMIC_PARAMETERS_API?: string;
    VITE_APP_ESIOS_API?: string;
    VITE_APP_CALC_API?: string;
    VITE_APP_DELTA_GPT_API?: string;
    VITE_APP_STORAGE_API?: string;
    VITE_APP_STORAGE_FORECASTING_API?: string;
    VITE_APP_ENACT_EUROPE_API?: string;
    VITE_APP_FUNCTIONAPP_CARBON_CALCULATOR_API?: string;
    VITE_APP_PRINT_PDF_API?: string;
    VITE_APP_ACCESS_API?: string;
    VITE_APP_EV_FLEX_API?: string;
    VITE_APP_CHATBOT_API?: string;
}

const env = (import.meta as unknown as { env: IApiEndpointEnv }).env;

function apiEndpoint (config: IApiEndpointConfig): IApiEndpointConfig {
    return config;
}

export const apiEndpointConfig = {
    main: apiEndpoint({
        envUrl: env.VITE_APP_MAIN_API,
        localUrl: 'https://localhost:44312/api',
    }),
    static: apiEndpoint({
        envUrl: env.VITE_APP_STATIC_API,
        localUrl: 'https://localhost:44318/staticApi',
    }),
    userSettings: apiEndpoint({
        envUrl: env.VITE_APP_USERSETTINGS_API,
        localUrl: 'https://localhost:44376/userSettingsApi',
    }),
    series: apiEndpoint({
        envUrl: env.VITE_APP_SERIES_API,
        localUrl: 'http://localhost:7071/api',
    }),
    epex: apiEndpoint({
        envUrl: env.VITE_APP_EPEX_API,
        localUrl: 'https://localhost:44397/epexApi',
    }),
    epexFrance: apiEndpoint({
        envUrl: env.VITE_APP_EPEX_FRANCE_API,
        localUrl: 'https://localhost:44397/epexApi',
    }),
    signalR: apiEndpoint({
        envUrl: env.VITE_APP_SIGNAL_R,
        localUrl: 'https://localhost:44330',
    }),
    entsoe: apiEndpoint({
        envUrl: env.VITE_APP_ENTSOE_API,
        localUrl: 'https://localhost:44339/entsoeApi',
    }),
    ireland: apiEndpoint({
        envUrl: env.VITE_APP_IRELAND_API,
        localUrl: 'https://localhost:44311/irelandApi',
    }),
    france: apiEndpoint({
        envUrl: env.VITE_APP_FRANCE_API,
        localUrl: 'https://localhost:44393/franceApi',
    }),
    belgium: apiEndpoint({
        envUrl: env.VITE_APP_BELGIUM_API,
        localUrl: 'https://localhost:44375/belgiumApi',
    }),
    netherlands: apiEndpoint({
        envUrl: env.VITE_APP_NETHERLANDS_API,
        localUrl: 'https://localhost:44342/netherlandsApi',
    }),
    dynamicParameters: apiEndpoint({
        envUrl: env.VITE_APP_DYNAMIC_PARAMETERS_API,
        localUrl: 'https://localhost:44396/dynamicParametersApi',
    }),
    esios: apiEndpoint({
        envUrl: env.VITE_APP_ESIOS_API,
        localUrl: 'https://localhost:44343/esiosApi',
    }),
    calc: apiEndpoint({
        envUrl: env.VITE_APP_CALC_API,
        localUrl: 'https://localhost:64505',
    }),
    deltaGpt: apiEndpoint({
        envUrl: env.VITE_APP_DELTA_GPT_API,
        localUrl: 'https://localhost:44356/api',
    }),
    storageTracking: apiEndpoint({
        envUrl: env.VITE_APP_STORAGE_API,
        localUrl: 'https://localhost:44305/api',
    }),
    storageForecasting: apiEndpoint({
        envUrl: env.VITE_APP_STORAGE_FORECASTING_API,
        localUrl: 'http://localhost:7072',
    }),
    powerMarketsEurope: apiEndpoint({
        envUrl: env.VITE_APP_ENACT_EUROPE_API,
        localUrl: 'https://localhost:44319/staticChartApi',
    }),
    carbonCalculatorApi: apiEndpoint({
        envUrl: env.VITE_APP_FUNCTIONAPP_CARBON_CALCULATOR_API,
        localUrl: 'http://localhost:7240/api',
    }),
    printPdfApi: apiEndpoint({
        envUrl: env.VITE_APP_PRINT_PDF_API,
        localUrl: env.VITE_APP_PRINT_PDF_API ?? '', // Not sure we'd ever need to run this locally.
    }),
    accessApi: apiEndpoint({
        envUrl: env.VITE_APP_ACCESS_API,
        localUrl: 'https://localhost:44316/api',
    }),
    flexIndex: apiEndpoint({
        envUrl: env.VITE_APP_EV_FLEX_API,
        localUrl: 'https://localhost:5175',
    }),
    chatBot: apiEndpoint({
        envUrl: env.VITE_APP_CHATBOT_API,
        localUrl: 'https://localhost:44386',
    }),
} satisfies Record<string, IApiEndpointConfig>;

export type TApiEndpointConfigKey = keyof typeof apiEndpointConfig;

export type TApiBaseUrlKey = Exclude<TApiEndpointConfigKey, 'signalR'> | 'dynamic';

function resolveApiEndpointUrl (config: IApiEndpointConfig): string {
    return config.envUrl?.trim() || config.localUrl;
}

export const apiEndpointUrls = Object.fromEntries(
    Object.entries(apiEndpointConfig).map(([key, config]) => [
        key,
        resolveApiEndpointUrl(config),
    ]),
) as Record<TApiEndpointConfigKey, string>;
